import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { ErreurApi } from '../../../api/client.js';
import { COULEURS, styleBouton } from '../../../app/styles.js';
import type { EntreeJournal, SourceJournal } from './source.js';

/**
 * Onglet « Journal » du dossier (PARITE-4, « Annexes » de la démo) : le journal horodaté complet, page après page, au-delà des
 * entrées que montre la vue d'ensemble. Lecture seule ; les heures sont celles de la Guadeloupe.
 */
const NATURES: Readonly<Record<EntreeJournal['kind'], string>> = {
  message: 'Message',
  appel: 'Appel',
  piece: 'Pièce',
  relance: 'Relance',
  decision: 'Décision',
  etape: 'Étape',
  consentement: 'Consentement',
};

const FORMAT = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'America/Guadeloupe',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});
const quand = (iso: string): string => {
  const p = Object.fromEntries(FORMAT.formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return `${p['day']}/${p['month']}/${p['year']} à ${p['hour']}:${p['minute']}`;
};
const messageDe = (e: unknown): string =>
  e instanceof ErreurApi ? e.messageUtilisateur : e instanceof Error ? e.message : 'Une erreur est survenue. Réessayez.';

const carte: CSSProperties = {
  background: COULEURS.blanc,
  border: '1px solid #E2E1EA',
  borderRadius: 12,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  minWidth: 0,
};
const discret: CSSProperties = { color: COULEURS.ardoise, fontSize: 13 };
const pastille: CSSProperties = {
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 600,
  background: '#EEF0F8',
  color: COULEURS.marine,
  borderRadius: 999,
  padding: '2px 10px',
};

export function VueJournal({ source }: { source: SourceJournal }) {
  const [entrees, setEntrees] = useState<EntreeJournal[] | null>(null);
  const [suite, setSuite] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const premiere = useCallback(async () => {
    setErreur(null);
    try {
      const p = await source.page();
      setEntrees(p.items);
      setSuite(p.nextCursor);
    } catch (e) {
      setErreur(messageDe(e));
    }
  }, [source]);

  useEffect(() => {
    void premiere();
  }, [premiere]);

  async function plusAnciennes(curseur: string): Promise<void> {
    setEnCours(true);
    setErreur(null);
    try {
      const p = await source.page(curseur);
      setEntrees((avant) => [...(avant ?? []), ...p.items]);
      setSuite(p.nextCursor);
    } catch (e) {
      setErreur(messageDe(e));
    } finally {
      setEnCours(false);
    }
  }

  if (entrees === null && erreur) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
        <p role="alert" style={{ margin: 0, color: COULEURS.rouge }}>
          {erreur}
        </p>
        <button type="button" style={styleBouton} onClick={() => void premiere()}>
          Réessayer
        </button>
      </div>
    );
  }
  if (entrees === null) return <p role="status">Chargement du journal…</p>;

  return (
    <section style={carte}>
      <h2 style={{ margin: 0, fontSize: 18, color: COULEURS.marine }}>Journal horodaté du dossier</h2>
      <p style={{ margin: 0, ...discret }}>Chaque action enregistrée sur le dossier, de la plus récente à la plus ancienne.</p>
      {entrees.length === 0 ? (
        <p style={{ margin: 0 }}>Aucune action n’a encore été enregistrée sur ce dossier.</p>
      ) : (
        <ol aria-label="Journal horodaté du dossier" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {entrees.map((e) => (
            <li
              key={e.id}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px 10px',
                padding: '8px 0',
                borderTop: '1px solid #EEEDF3',
                overflowWrap: 'anywhere',
              }}
            >
              <span style={{ ...discret, flex: '0 0 auto' }}>{quand(e.at)}</span>
              <span style={pastille}>{NATURES[e.kind] ?? e.kind}</span>
              {e.visibility === 'equipe' ? (
                <span style={{ ...pastille, background: '#F1EFF6', color: COULEURS.ardoise }}>Équipe seulement</span>
              ) : null}
              <span style={{ flex: '1 1 260px', minWidth: 0 }}>{e.summary}</span>
            </li>
          ))}
        </ol>
      )}
      {erreur ? (
        <p role="alert" style={{ margin: 0, color: COULEURS.rouge }}>
          {erreur}
        </p>
      ) : null}
      {suite ? (
        <button
          type="button"
          style={{ ...styleBouton, alignSelf: 'flex-start' }}
          disabled={enCours}
          onClick={() => void plusAnciennes(suite)}
        >
          Afficher les entrées plus anciennes
        </button>
      ) : null}
    </section>
  );
}
